import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabaseClient";

let sessionHydrationPromise: ReturnType<typeof supabase.auth.getSession> | null = null;
let authSubscription: any | null = null;

function getSessionOnce() {
  if (!sessionHydrationPromise) {
    sessionHydrationPromise = supabase.auth.getSession().finally(() => {
      sessionHydrationPromise = null;
    });
  }
  return sessionHydrationPromise;
}

function pickDisplayName(user: any): string {
  const m = user?.user_metadata ?? {};
  const candidate =
    m.full_name ??
    m.name ??
    m.display_name ??
    m.preferred_name ??
    user?.email ??
    "";
  return typeof candidate === "string" ? candidate.trim() : "";
}

function pickFirstName(user: any): string {
  const m = user?.user_metadata ?? {};
  const raw =
    m.first_name ??
    m.given_name ??
    m.full_name ??
    m.name ??
    m.display_name ??
    m.preferred_name ??
    "";

  const s = typeof raw === "string" ? raw.trim() : "";
  if (!s) return "";

  if (s.includes("@")) return s.split("@")[0].trim();
  return s.split(/\s+/)[0]?.trim() ?? "";
}

function composeEmployeeFullName(
  firstName: string | null | undefined,
  middleNames: string | null | undefined,
  lastName: string | null | undefined
): string {
  return [firstName?.trim(), middleNames?.trim(), lastName?.trim()]
    .filter(Boolean)
    .join(" ");
}

type EmployeeIdentity = {
  employeeCompanyId: string | null;
  employeeSystemId: string | null;
  employeeFirstName: string | null;
};

type AuthState = {
  initializing: boolean;
  user: any | null;
  session: any | null;
  isAuthenticated: boolean;
  displayName: string;
  firstName: string;
  employeeCompanyId: string | null;
  employeeSystemId: string | null;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [initializing, setInitializing] = useState(true);
  const [session, setSession] = useState<any | null>(null);
  const [user, setUser] = useState<any | null>(null);
  const [employeeIdentity, setEmployeeIdentity] = useState<EmployeeIdentity>({
    employeeCompanyId: null,
    employeeSystemId: null,
    employeeFirstName: null,
  });

  async function resolveEmployeeIdentity(activeUser: any | null) {
    if (!activeUser?.id) {
      setEmployeeIdentity({
        employeeCompanyId: null,
        employeeSystemId: null,
        employeeFirstName: null,
      });
      return;
    }

    const metadata = (activeUser.user_metadata ?? {}) as Record<string, unknown>;
    const metadataCompanyId =
      typeof metadata.company_id === "string" && metadata.company_id.trim().length > 0
        ? metadata.company_id.trim()
        : null;

    let query = supabase
      .from("employee_profiles")
      .select(
        "company_id, custom_employee_id, internal_employee_sequence_number, first_name, middle_names, last_name, full_name"
      )
      .eq("auth_user_id", activeUser.id);

    if (metadataCompanyId) {
      query = query.eq("company_id", metadataCompanyId);
    }

    const { data, error } = await query.maybeSingle();

    if (error || !data) {
      setEmployeeIdentity({
        employeeCompanyId: null,
        employeeSystemId: null,
        employeeFirstName: null,
      });
      return;
    }

    const customEmployeeId =
      typeof data.custom_employee_id === "string" && data.custom_employee_id.trim().length > 0
        ? data.custom_employee_id.trim()
        : null;

    const internalPaycalcId =
      typeof data.internal_employee_sequence_number === "number"
        ? String(data.internal_employee_sequence_number)
        : null;

    const composedFullName = composeEmployeeFullName(
      typeof data.first_name === "string" ? data.first_name : null,
      typeof data.middle_names === "string" ? data.middle_names : null,
      typeof data.last_name === "string" ? data.last_name : null
    );

    const resolvedEmployeeFirstName =
      typeof data.first_name === "string" && data.first_name.trim().length > 0
        ? data.first_name.trim()
        : composedFullName.trim().length > 0
          ? composedFullName.trim().split(/\s+/)[0]?.trim() ?? null
          : typeof data.full_name === "string" && data.full_name.trim().length > 0
            ? data.full_name.trim().split(/\s+/)[0]?.trim() ?? null
            : null;

    setEmployeeIdentity({
      employeeCompanyId:
        typeof data.company_id === "string" && data.company_id.trim().length > 0
          ? data.company_id
          : metadataCompanyId,
      employeeSystemId: customEmployeeId ?? internalPaycalcId,
      employeeFirstName: resolvedEmployeeFirstName,
    });
  }

  async function hydrateFromSupabase() {
    const { data, error } = await getSessionOnce();

    if (error) {
      setSession(null);
      setUser(null);
      setEmployeeIdentity({
        employeeCompanyId: null,
        employeeSystemId: null,
        employeeFirstName: null,
      });
      return;
    }

    const resolvedUser = data.session?.user ?? null;

    setSession(data.session ?? null);
    setUser(resolvedUser);
    await resolveEmployeeIdentity(resolvedUser);
  }

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        await hydrateFromSupabase();
      } finally {
        if (mounted) setInitializing(false);
      }
    })();

    // ✅ singleton auth subscription
    if (!authSubscription) {
      const { data: sub } = supabase.auth.onAuthStateChange(
        (_event, newSession) => {
          const nextUser = newSession?.user ?? null;
          setSession(newSession ?? null);
          setUser(nextUser);
          void resolveEmployeeIdentity(nextUser);
          setInitializing(false);
        }
      );
      authSubscription = sub;
    }

    return () => {
      mounted = false;
    };
  }, []);

  const displayName = useMemo(() => pickDisplayName(user), [user]);
  const firstName = useMemo(
    () => employeeIdentity.employeeFirstName ?? pickFirstName(user),
    [employeeIdentity.employeeFirstName, user]
  );

  const value = useMemo<AuthState>(
    () => ({
      initializing,
      user,
      session,
      isAuthenticated: !!session?.user,
      displayName,
      firstName,
      employeeCompanyId: employeeIdentity.employeeCompanyId,
      employeeSystemId: employeeIdentity.employeeSystemId,
      refresh: async () => {
        await hydrateFromSupabase();
      },
      signOut: async () => {
        await supabase.auth.signOut();

        try {
          localStorage.removeItem("active_company_id");
        } catch {
          // ignore
        }

        setSession(null);
        setUser(null);
        setEmployeeIdentity({
          employeeCompanyId: null,
          employeeSystemId: null,
          employeeFirstName: null,
        });
      },
    }),
    [initializing, session, user, displayName, firstName, employeeIdentity]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}